import React from 'react';
import css from 'styled-jsx/css';
import classnames from 'classnames';
import { colors, dimensions } from '../common/Theme';
import PropTypes from 'prop-types';
import { formatter } from '@yupwego/string-fns';

const RADIUS = '16px';
const CONTAINER_SPACE = '16px';
const BORDER_SIZE = '4px';

const subscriptionStyles = css`
  .subscription {
    display: flex;
    margin: 15px 0;
    flex-direction: column;
    list-style: none;
  }

  .subscription__item {
    display: flex;
    flex-direction: column;
  }

  .subscription__item-start {
    position: relative;
    flex-grow: 1;
  }

  .subscription__item-start::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    content: "";
    z-index: 10;
  }

  .subscription-header {
    display: block;
    margin: 0;
    padding 16px;
    color: white;
    background-color: #354052;
  }

  .country {
    border-radius: ${RADIUS} ${RADIUS} 0 0;
  }

  .subscription-container {
    display: flex;
    padding: ${CONTAINER_SPACE};
    flex-direction: column;
    flex-grow: 1;
    justify-content: center;
  }

  .subscription-infos,
  .profile-actions {
    border: ${BORDER_SIZE} solid ${colors.ywgVeryLightBlue};
    border-top-width: 0;
  }

  .subscription-infos {
    position: relative;
    border-bottom-width: 0;
  }

  .profile-actions {
    border-bottom-left-radius: ${RADIUS};
    border-bottom-right-radius: ${RADIUS};
  }

  .subscription-infos::before {
    margin: -30px auto;
    width: 49px;
    height: 142px;
    content: "";
    background: url(/static/images/barcode.svg) center no-repeat;
    transform: rotate(90deg);
  }

  .key,
  .value {
    display: inline-block;
  }

  .value {
    margin: 0;
    font-weight: 700;
  }

  .subscription-header-item {
    font-size: 0.875rem;
  }

  .stay-start-key,
  .stay-start-value {
    margin-bottom: 32px;
    font-size: 0.875rem;
  }

  .destination-zone {
    font-weight: normal;
  }

  .beneficiaries-key {
    margin-bottom: 8px;
    font-size: 0.875rem;
  }

  .beneficiaries-value {
    margin: 0;
  }

  .beneficiaries {
    margin: 0;
    padding: 0;
    font-size: 0.875rem;
    list-style: none;
  }

  .beneficiary:not(:last-of-type) {
    margin-bottom: 8px;
  }

  .beneficiary-name {
    font-size: 0.875rem;
    font-weight: 700;
  }

  .price {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    gap: 10px;
  }

  @media (min-width: ${dimensions.ywgProfileMaxWidth}) {
    .subscription {
      flex-direction: row;
    }

    .subscription-header {
      padding 24px;
      border-radius: ${RADIUS} 0 0;
    }

    .subscription-header-item {
      font-size: 1.125rem;
    }

    .subscription__item-start::after {
      border-right: 3px dashed ${colors.ywgVeryLightBlue};
    }

    .subscription__item-end {
      width: 330px;
    }

    .price {
      border-radius: 0 ${RADIUS} 0 0;
    }

    .beneficiary-name {
      font-size: 1.125rem;
      font-weight: normal;
    }

    .subscription-infos {
      padding-left: 100px;
      min-height: 130px;
      border-right-width: 0;
      border-bottom-left-radius: ${RADIUS};
      border-bottom-width: ${BORDER_SIZE};
    }

    .subscription-infos::before {
      position: absolute;
      margin: 0 0 0 ${RADIUS};
      left: 0;
      transform: initial;
    }

    .profile-actions {
      border-left-width: 0;
      border-bottom-left-radius: 0;
    }

    @supports(grid-template-rows: subgrid) {
      .subscription {
        display: grid;
        grid-template-columns: 1fr minmax(170px, 32%);
      }

      .subscription__item {
        display: grid;
        grid-template-rows: subgrid;
        grid-row: 2 / 4;
      }

      .subscription__item-end {
        width: auto;
      }
    }
  }
`;

const subscriptionDynamicStyles = css.resolve`
  .profile__action {
    margin: 0 0 10px;
    list-style: none;
  }

  .profile__action:last-child {
    margin-bottom: 0;
  }
`;

const Subscription = ({ subscription, children }) => {
  const actionClassName = classnames(
    'profile__action',
    subscriptionDynamicStyles.className
  );
  return (
    <>
      <li key={subscription.id} className="subscription">
        <div className="subscription__item subscription__item-start">
          <dl className="subscription-header country">
            <dt className="key subscription-header-item">Pays :</dt>{' '}
            <dd className="value subscription-header-item">
              {subscription.stay.destination}{' '}
              <span className="destination-zone">
                (Zone {subscription.quote.zone})
              </span>
            </dd>
          </dl>
          <div className="subscription-container subscription-infos">
            <dl>
              <dt className="key stay-start-key">Date de départ :</dt>{' '}
              <dd className="value stay-start-value">
                {formatter(subscription.stay.startDate)}
              </dd>
              <dt className="beneficiaries-key">Voyageurs</dt>
              <dd className="beneficiaries-value">
                <ul className="beneficiaries">
                  {subscription.beneficiaries.map(
                    ({ key, firstname, lastname, birthday }) => (
                      <li
                        key={`profile-traveller-${key}`}
                        className="beneficiary"
                      >
                        <span className="beneficiary-name">
                          {firstname} {lastname}
                        </span>{' '}
                        - {formatter(birthday)}
                      </li>
                    )
                  )}
                </ul>
              </dd>
            </dl>
          </div>
        </div>
        <div className="subscription__item subscription__item-end">
          <dl className="subscription-header price">
            <dt className="key subscription-header-item">Total payé :</dt>{' '}
            <dd className="value subscription-header-item">
              {subscription.quote.total.toFixed(2)} €
            </dd>
          </dl>
          {children ? (
            <ul className="subscription-container profile-actions">
              {children(actionClassName, subscription)}{' '}
            </ul>
          ) : null}
        </div>
      </li>
      {subscriptionDynamicStyles.styles}
      <style jsx>{subscriptionStyles}</style>
    </>
  );
};

Subscription.propTypes = {
  subscription: PropTypes.object.isRequired,
  children: PropTypes.func
};

export default Subscription;
